import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Sparkles } from "lucide-react";
import { TID } from "@/lib/testIds";

const links = [
  { to: "/", label: "Home", tid: TID.navHome, end: true },
  { to: "/trailers", label: "Our Trailers", tid: TID.navTrailers },
  { to: "/events", label: "Events", tid: TID.navEvents },
  { to: "/how-it-works", label: "How It Works", tid: TID.navHow },
  { to: "/gallery", label: "Gallery", tid: TID.navGallery },
  { to: "/service-area", label: "Service Area", tid: TID.navServiceArea },
  { to: "/faqs", label: "FAQs", tid: TID.navFaqs },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled ? "backdrop-blur-xl bg-ivory/80 border-b border-stone" : "bg-transparent"}`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
          <Sparkles className="h-5 w-5 text-pebble transition-transform group-hover:rotate-12" />
          <div className="leading-tight">
            <div className="h-display text-lg md:text-xl">Southern Utah Event Rentals</div>
            <div className="overline text-[10px]">Luxury Portable Restrooms</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.tid}
              className={({ isActive }) =>
                `text-sm tracking-wide relative transition-colors ${isActive ? "text-charcoal" : "text-graphite hover:text-charcoal"}`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span className={`absolute -bottom-1.5 left-0 h-px bg-pebble transition-[width] duration-300 ${isActive ? "w-full" : "w-0"}`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/booking" className="btn-primary hidden md:inline-flex" data-testid={TID.navBookCta}>
            Request a Booking
          </Link>
          <button
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full border border-stone bg-white"
            onClick={() => setOpen((v) => !v)}
            data-testid={TID.navMobileToggle}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-ivory border-t border-stone">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`${l.tid}-mobile`}
                className={({ isActive }) => `text-base ${isActive ? "text-charcoal font-medium" : "text-graphite"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/booking" className="btn-primary w-full mt-2" data-testid={`${TID.navBookCta}-mobile`}>
              Request a Booking
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
